import { useEffect, useMemo, useState } from "react";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { Heading, Text } from "../../../components/common/typography";
import { RadioOption } from "../../../components/common/radio-option";
import { Button } from "../../../components/common/button";
import { Chip } from "../../../components/common/chip";
import { ShimmerLoader } from "../../../components/common/shimmer";
import {
  useMyAddresses,
  useAddressMutations,
  type BuyerAddressType,
} from "../hooks/useMyAddresses";
import { AddressForm } from "./address-form";
import { showErrorToast, showSuccessToast } from "../../../utils/toasts";
import type { BuyerAddressDto } from "../../../api";

function formatAddress(a: BuyerAddressDto): string {
  return [a.street, a.street2, [a.zip, a.city].filter(Boolean).join(" "), a.countryName]
    .filter(Boolean)
    .join(", ");
}

interface Copy {
  heading: string;
  emptyHint: string;
  addLabel: string;
  loadingMessage: string;
}

const COPY: Record<BuyerAddressType, Copy> = {
  delivery: {
    heading: "Delivery Address",
    emptyHint:
      "You don't have any saved delivery addresses yet. Add one to continue.",
    addLabel: "Add new delivery address",
    loadingMessage: "Loading your saved delivery addresses…",
  },
  invoice: {
    heading: "Billing Address",
    emptyHint:
      "You don't have any saved billing addresses yet. Add one to use a different bill-to.",
    addLabel: "Add new billing address",
    loadingMessage: "Loading your saved billing addresses…",
  },
  other: {
    heading: "Saved Addresses",
    emptyHint: "You don't have any saved addresses yet.",
    addLabel: "Add new address",
    loadingMessage: "Loading your saved addresses…",
  },
};

interface PickerProps {
  /**
   * `picker` (default) — radio-list selection, parent owns the selected id.
   *   Used by checkout (delivery) and payment (billing).
   * `manage` — plain card list with no radio. Parent passes no selection.
   *   Used by settings, where the user just lists / adds / archives.
   */
  mode?: "picker";
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  /**
   * Auto-select the first address once they load. Default `true`. Disable
   * when the parent wants the user to make an explicit choice (e.g. an
   * empty billing list when "Same as delivery" is unchecked).
   */
  autoSelectFirst?: boolean;
}

interface ManageProps {
  mode: "manage";
  selectedId?: never;
  onSelect?: never;
  autoSelectFirst?: never;
}

type Props = (PickerProps | ManageProps) & {
  type: BuyerAddressType;
  /** Override default heading. */
  heading?: string;
};

/**
 * Type-parameterized address book picker. Renders the buyer's saved
 * delivery / invoice / other addresses, lets them pick one, archive one,
 * or inline-add a new one. Used by checkout (delivery), payment (billing),
 * and settings (other locations).
 *
 * Stateless about which id is selected — the parent owns that. This keeps
 * the component reusable across stores (checkout vs. settings vs. ad-hoc).
 */
export function AddressBookSection(props: Props) {
  const { type, heading } = props;
  const isPicker = props.mode !== "manage";
  const selectedId = isPicker ? props.selectedId : null;
  const onSelect = isPicker ? props.onSelect : undefined;
  const autoSelectFirst = isPicker ? props.autoSelectFirst ?? true : false;

  const { data: addresses, isLoading, error } = useMyAddresses(type);
  const { archive, setDefault, isArchiving, isSettingDefault } = useAddressMutations();
  const [isAddingNew, setIsAddingNew] = useState(false);
  const copy = COPY[type];

  // Keep the parent's selection pointing at an address in this book. The
  // checkout store outlives the page, so it can still hold an id that was
  // archived since, belongs to another account, or was seeded from a sale's
  // company partner — sending that one to the BE fails with "Address N not
  // found". Replace it with the first address (unless the parent opts out of
  // auto-select), or clear it when there's nothing to pick.
  useEffect(() => {
    if (!onSelect || !addresses) return;
    if (selectedId != null && addresses.some((a) => a.id === selectedId)) return;
    const fallback = autoSelectFirst ? addresses[0]?.id ?? null : null;
    if (fallback !== selectedId) onSelect(fallback);
  }, [addresses, selectedId, onSelect, autoSelectFirst]);

  const hasSaved = useMemo(() => (addresses?.length ?? 0) > 0, [addresses]);

  const handleArchive = async (id: number) => {
    try {
      await archive(id);
      if (selectedId === id) onSelect?.(null);
      showSuccessToast("Address removed");
    } catch (err: any) {
      showErrorToast(
        err?.response?.data?.message ?? err?.message ?? "Could not remove address.",
      );
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await setDefault(id);
      showSuccessToast("Default delivery address updated");
    } catch (err) {
      const typed = err as { response?: { data?: { message?: string } } };
      showErrorToast(
        typed?.response?.data?.message ?? "Could not set the default address.",
        err,
      );
    }
  };

  // Only delivery addresses have a default (checkout pre-selects it).
  const supportsDefault = type === "delivery";

  const renderRow = (addr: BuyerAddressDto) => {
    const body = (
      <div className="flex w-full items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <Text variant="bodyBold" tone="dark">{addr.name}</Text>
            {supportsDefault && addr.isDefault && <Chip variant="secondary">Default</Chip>}
          </div>
          <Text variant="bodySm" tone="muted">{formatAddress(addr) || "—"}</Text>
          {addr.phone && (
            <Text variant="bodyXs" tone="muted">{addr.phone}</Text>
          )}
          {supportsDefault && !isPicker && !addr.isDefault && (
            <Button
              variant="underlinedGhost"
              size="sm"
              className="self-start"
              disabled={isSettingDefault}
              onClick={() => void handleSetDefault(addr.id)}
            >
              Set as default
            </Button>
          )}
        </div>
        {/*
          Rendered as a span (not a <button>) because in `picker` mode this
          row is wrapped in <RadioOption>, which is itself a <button>. HTML
          forbids nested buttons; a div/span with role=button + keyboard
          handlers gives the same UX without the React DOM warning.
        */}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            if (isArchiving) return;
            void handleArchive(addr.id);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            e.stopPropagation();
            if (isArchiving) return;
            void handleArchive(addr.id);
          }}
          aria-disabled={isArchiving}
          aria-label={`Remove ${addr.name}`}
          className={`cursor-pointer text-textMuted transition-colors hover:text-accentError ${
            isArchiving ? "pointer-events-none opacity-50" : ""
          }`}
        >
          <IconTrash size={18} />
        </span>
      </div>
    );

    if (isPicker) {
      return (
        <RadioOption
          key={addr.id}
          selected={selectedId === addr.id}
          onClick={() => onSelect?.(addr.id)}
          align="start"
        >
          {body}
        </RadioOption>
      );
    }

    return (
      <div
        key={addr.id}
        className="rounded-2xl border border-borderDefault bg-bgWhite px-4 py-3"
      >
        {body}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <Heading variant="h3" tone="dark">
        {heading ?? copy.heading}
      </Heading>

      {isLoading ? (
        <ShimmerLoader message={copy.loadingMessage} />
      ) : error ? (
        <Text variant="bodySm" tone="inherit" className="text-accentError">
          Couldn't load your addresses. Please refresh the page.
        </Text>
      ) : (
        <>
          {(addresses ?? []).map(renderRow)}

          {!hasSaved && !isAddingNew && (
            <Text variant="bodySm" tone="muted">
              {copy.emptyHint}
            </Text>
          )}

          {!isAddingNew ? (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<IconPlus size={16} />}
              onClick={() => setIsAddingNew(true)}
              className="self-start"
            >
              {copy.addLabel}
            </Button>
          ) : (
            <div className="rounded-2xl border border-borderDefault bg-bgWhite p-5">
              <AddressForm
                type={type}
                onCancel={() => setIsAddingNew(false)}
                onCreated={(created) => {
                  onSelect?.(created.id);
                  setIsAddingNew(false);
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
